import { CA } from 'src/app/shared/entities/ca';
import { CryptoToken } from 'src/app/shared/entities/crypto-token';
import { Profile } from 'src/app/shared/entities/profile';
import { CreateCaFullDto } from 'src/app/shared/services/api/dto/create-ca-full-dto';
import { yearsFromNowToDays } from 'src/app/shared/util/years-from-now-to-days';

export enum CaFullFormField {
  caType = 'caType',
  cryptoTokenType = 'cryptoTokenType',
  cryptoTokenName = 'cryptoTokenName',
  dn = 'dn',
  signedBy = 'signedBy',
  keySpecification = 'keySpecification',
  validityYears = 'validityYears',
  certificateMaxValidityYears = 'certificateMaxValidityYears',
  profileName = 'profileName',
  caCertificateProfile = 'caCertificateProfile',
  useLdapDnOrder = 'useLdapDnOrder',
  password = 'password',
  signingAlgo = 'signingAlgo',
}

export interface CaFullFormValue {
  [CaFullFormField.caType]?: string | null;
  [CaFullFormField.caCertificateProfile]?: string | null;
  [CaFullFormField.profileName]?: string | null;
  [CaFullFormField.dn]?: string | null;
  // Sync-up 2025-09-02: dropdown for years
  [CaFullFormField.validityYears]?: string | null;
  [CaFullFormField.cryptoTokenName]?: string | null;
  [CaFullFormField.password]?: string | null;
  [CaFullFormField.cryptoTokenType]?: string | null;
  [CaFullFormField.signedBy]?: string | null;
  [CaFullFormField.keySpecification]?: string | null;
  [CaFullFormField.certificateMaxValidityYears]?: string | null;
  [CaFullFormField.useLdapDnOrder]?: boolean | null;
  [CaFullFormField.signingAlgo]?: string | null;
}

export interface CaFullFormExternalData {
  cas: CA[];
  profiles: Profile[];
  cryptotokens: CryptoToken[];
}

export enum CaType {
  // lhs: business names; rhs: what BE expects
  ICA = 'SUB',
  Root = 'ROOT',
}

export function formValueToDto(formValue: CaFullFormValue): CreateCaFullDto {
  if (!formValue[CaFullFormField.caType]) {
    throw new Error('This should not happen: empty caType');
  }
  if (!formValue[CaFullFormField.caCertificateProfile]) {
    throw new Error('This should not happen: empty caCertificateProfile');
  }
  if (!formValue[CaFullFormField.dn]) {
    throw new Error('This should not happen: empty dn');
  }
  if (!formValue[CaFullFormField.cryptoTokenType]) {
    throw new Error('This should not happen: empty cryptoTokenTyp');
  }
  if (!formValue[CaFullFormField.keySpecification]) {
    throw new Error('This should not happen: empty keySpecification');
  }
  if (
    formValue[CaFullFormField.validityYears] === null ||
    formValue[CaFullFormField.validityYears] === undefined
  ) {
    throw new Error('This should not happen: null validityYears');
  }
  if (!formValue[CaFullFormField.signingAlgo]) {
    throw new Error('This should not happen: empty signingAlgo');
  }
  const certificateMaxValidityYears = parseInt(
    formValue[CaFullFormField.certificateMaxValidityYears] ?? '',
  );
  const maxCertificateValidityInDays = Number.isNaN(certificateMaxValidityYears)
    ? null
    : yearsFromNowToDays(certificateMaxValidityYears);
  return {
    caType: formValue[CaFullFormField.caType],
    caCertificateProfile: formValue[CaFullFormField.caCertificateProfile],
    profileName: formValue[CaFullFormField.profileName] ?? null,
    dn: formValue[CaFullFormField.dn],
    signedBy: formValue[CaFullFormField.signedBy] ?? null,
    validityDays: yearsFromNowToDays(
      parseInt(formValue[CaFullFormField.validityYears]),
    ),
    cryptoTokenName: formValue[CaFullFormField.cryptoTokenName] || null,
    password: formValue[CaFullFormField.password] || null,
    cryptoTokenType: formValue[CaFullFormField.cryptoTokenType],
    // keyAlgorithm: '',
    keySpecification: formValue[CaFullFormField.keySpecification],
    maxCertificateValidityInDays,
    useLdapDnOrder: !!formValue[CaFullFormField.useLdapDnOrder],
    signingAlgo: formValue[CaFullFormField.signingAlgo],
  };
}
